import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ExternalLink, LogOut, Bell, User, Settings, Search, Filter } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { InvestorBusinessList } from '../../components/investor/InvestorBusinessList';
import { InvestorDashboardMetrics } from '../../components/investor/InvestorDashboardMetrics';
import { ensureUserMetrics } from '@/utils/authHelpers';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { AssignedDocuments } from '@/components/shared/AssignedDocuments';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ConversationsTab } from '@/components/conversations/ConversationsTab';

const InvestorPage = () => {
  // State hooks
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('businesses');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  
  // Context hooks
  const { user, signOut } = useAuth();

  // Effect hooks
  useEffect(() => {
    // Initialize investor metrics if needed
    if (user) {
      ensureUserMetrics(user.id);
      fetchUserProfile(user.id);
      fetchNotifications(user.id);
    }
    
    // Simulate loading to ensure components are properly mounted
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [user]);
  
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };
  
  const fetchNotifications = async (userId: string) => {
    try {
      // This is a placeholder - in a real app, you would fetch actual notifications
      // from a notifications table in your database
      setNotifications([
        { id: 1, message: 'New investment opportunity from TechStartup Inc.', isRead: false },
        { id: 2, message: 'Your watchlist company GreenEnergy has updated their pitch', isRead: true },
        { id: 3, message: 'Quarterly report available for your investments', isRead: true }
      ]);
      
      // Check if there are any unread notifications
      const unreadCount = notifications.filter(n => !n.isRead).length;
      setHasNewNotifications(unreadCount > 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 space-y-8 bg-white">
        <Skeleton className="h-10 w-64 mb-8" />
        <Skeleton className="h-8 w-full max-w-md mb-6" />
        <div className="space-y-6">
          <Skeleton className="h-[400px] w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <div className="container mx-auto pt-12 pb-8 px-8 space-y-8 bg-background">
      <div className="flex flex-col space-y-6 mb-8">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-4">
            <Logo className="h-12 w-auto" />
            <h1 className="text-3xl font-bold">Investor Dashboard</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <Button variant="ghost" className="relative p-2" onClick={() => setHasNewNotifications(false)}>
                <Bell size={20} />
                {hasNewNotifications && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src="/placeholder-avatar.jpg" alt="User" />
                <AvatarFallback>{userProfile?.full_name?.charAt(0) || user?.email?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <p className="text-sm font-medium">{userProfile?.full_name || user?.email}</p>
                <p className="text-xs text-muted-foreground">Investor</p>
              </div>
            </div>
            
            <Button variant="destructive" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">Discover and explore investment opportunities</p>
          <div className="flex space-x-4">
            <Button variant="outline" asChild>
              <Link to="/" className="flex items-center gap-2">
                <ExternalLink size={18} />
                View Website
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/settings" className="flex items-center gap-2">
                <Settings size={18} />
                Settings
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Dashboard Metrics */}
      <InvestorDashboardMetrics />
      
      <Tabs defaultValue="businesses" className="space-y-4" value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="businesses">Investment Opportunities</TabsTrigger>
            <TabsTrigger value="portfolio">My Portfolio</TabsTrigger>
            <TabsTrigger value="watchlist">My Watchlist</TabsTrigger>
            <TabsTrigger value="documents">My Documents</TabsTrigger>
            <TabsTrigger value="conversations">Conversations</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search opportunities..."
                className="pl-8 w-[200px] md:w-[300px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Filter size={16} />
              <span className="hidden sm:inline">Filter</span>
            </Button>
          </div>
        </div>

        <TabsContent value="businesses" className="space-y-4">
          <div className="flex gap-2 flex-wrap mb-4">
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">All Industries</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">Technology</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">Healthcare</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">Finance</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">Education</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-secondary">Sustainability</Badge>
          </div>
          <InvestorBusinessList />
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-4">
          <h2 className="text-2xl font-bold">My Portfolio</h2>
          <div className="p-6 text-center bg-secondary/10 rounded-lg">
            <p className="text-muted-foreground">You haven't invested in any businesses yet.</p>
            <p className="text-muted-foreground">Explore the Investment Opportunities tab to find businesses to invest in.</p>
            <Button className="mt-4" onClick={() => setActiveTab('businesses')}>Browse Opportunities</Button>
          </div>
        </TabsContent>
        
        <TabsContent value="watchlist" className="space-y-4">
          <h2 className="text-2xl font-bold">My Watchlist</h2>
          <div className="p-6 text-center bg-secondary/10 rounded-lg">
            <p className="text-muted-foreground">Your watchlist is empty.</p>
            <p className="text-muted-foreground">Add businesses to your watchlist to track them.</p>
            <Button className="mt-4" onClick={() => setActiveTab('businesses')}>Browse Opportunities</Button>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <h2 className="text-2xl font-bold">My Documents</h2>
          <AssignedDocuments />
        </TabsContent>

        <TabsContent value="conversations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
              <CardDescription>Communicate with entrepreneurs and administrators</CardDescription>
            </CardHeader>
            <CardContent>
              {user && <ConversationsTab userId={user.id} userRole="investor" />}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvestorPage;
